import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { UtilityService } from 'src/app/utils/utility.service';
import { WizerValidatorService } from 'src/app/utils/wizer.validators.service';
import { CurrencyFormatterService } from 'src/app/shared/services/currency-formatter.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { FloatLabelType } from '@angular/material/form-field';
import {
  ActionButtonType,
  KeyValueType,
  ReportInputTypes,
  reportSummaryType,
} from '../../models/common';
import { AuthService } from 'src/app/auth/services/auth.service';
import { PermissionService } from 'src/app/utils/permission.service';
import { Permissions } from 'src/app/shared/dto/permissions';

@Component({
  selector: 'app-reports-board',
  templateUrl: './reports-board.component.html',
  styleUrls: ['./reports-board.component.css'],
})
export class ReportsBoardComponent implements OnInit, OnChanges {
  // summaryData split for the mobile pill: stats (rendered in the pill,
  // grouped 3 per row with dividers) and non-stats like the report run date
  // (rendered below the pill).
  public statItems: reportSummaryType[] = [];
  public dateItems: reportSummaryType[] = [];
  @Input() showLoanOfficerDefaultListValue: boolean = true;
  @Input() showDepositDefaultListValue: boolean = true;
  @Input() showDepositClientDefaultListValue: boolean = true;
  @Input() inputsToShow: Array<ReportInputTypes>;
  // On mobile, render the filter fields 2 per row instead of the default 3
  // (for reports whose filters read better in two columns).
  @Input() mobileTwoColumns: boolean = false;
  // On mobile, render every filter field (dates included) 3 per row.
  @Input() mobileThreeColumns: boolean = false;
  // Toggles the SME option in the Customer Type filter (some reports only
  // support Individual/Corporate). Defaults to true so existing reports are
  // unaffected.
  @Input() showSmeCustomerType: boolean = true;
  @Input() savedData;
  @Input() showTableActionFilter: boolean = true;
  @Input() showSummaryData: boolean = true;
  @Input() summaryData: reportSummaryType[];
  @Input() clientData: { id: number; first_name: string; last_name: string }[] =
    [];
  @Input() depositClientData: {
    id: number;
    first_name: string;
    last_name: string;
  }[] = [];
  @Input() payload = [];
  @Input() type: 'client' | 'server' = 'client';
  @Input() minDate = new Date('01/01/2016');
  @Input() maxDate = new Date();
  @Input() title: string;
  @Input() activeState: string;
  @Input() initialValue: string = '';
  @Input() states: any[];
  @Input() actionButtons: ActionButtonType[];
  @Input() showActionBar2: boolean = true;
  @Input() disableActionButtons: boolean = true;
  @Input() showSearchInput: boolean = true;
  @Input() showDownloadButton: any;
  @Input() runText: string = $localize`:@@fetch_records:Fetch Records`;
  @Input() runIconPath: string = 'assets/icons/download.svg';
  @Input() runMenu: KeyValueType[] = [
    { key: $localize`:@@download_as_excel:Download as Excel`, value: 'csv' },
  ];
  @Input() showRunButton: boolean = false;
  @Input() disableRunButton: boolean = false;
  @Input() showNumOfRowSelected: boolean = false;
  @Input() numOfRowSelected: number = 0;
  @Input() totalAmtOfRowSelected: number = 0;
  @Input() shouldShowDownloadAndPadgination: any;

  @Output() searchEmit: EventEmitter<any> = new EventEmitter();
  @Output() stateEmit: EventEmitter<any> = new EventEmitter();
  @Output() actionEmit: EventEmitter<any> = new EventEmitter();
  @Output() runMainMenu: EventEmitter<null> = new EventEmitter();
  @Output() runMenuChange: EventEmitter<{ val: string; formValues: any }> =
    new EventEmitter();

  @Output() setSelectedFilter: EventEmitter<any> = new EventEmitter();
  @Output() setInputedFilter: EventEmitter<any> = new EventEmitter();
  @Output() runReportEmit: EventEmitter<any> = new EventEmitter();
  @Output() emitChange: EventEmitter<any> = new EventEmitter();

  public reportsForm: FormGroup;
  public reportInputTypes = ReportInputTypes;
  public selectedAreaId: number = 0;
  public selectedBranchId: number = 0;
  public selectedLoanOfficerId: number = 0;
  public canViewOtherAreas: boolean = false;
  public canViewOtherBranches: boolean = false;
  public canViewLoanOfficerFilter: boolean = false;
  public defaultArea: { id: number; name: string } | null = null;
  public defaultBranch: { id: number; name: string } | null = null;
  public defaultAll = {
    ['name']: $localize`:@@all:All`,
    ['id']: 0,
  };
  public startDateRange = {
    min: new Date('01/01/2016'),
    max: new Date(),
  };
  public endDateRange = {
    min: new Date(this.startDateRange.max),
    max: new Date(),
  };

  public reportDays = [
    { key: 'Today', value: 'TODAY' },
    { key: '1 Day Ago', value: '1_DAY_AGO' },
    { key: '2 Days Ago', value: '2_DAYS_AGO' },
    { key: '3 Days Ago', value: '3_DAYS_AGO' },
    { key: '4 Days Ago', value: '4_DAYS_AGO' },
    { key: '5 Days Ago', value: '5_DAYS_AGO' },
    { key: '6 Days Ago', value: '6_DAYS_AGO' },
  ];

  // On mobile the filter labels always float so they don't get clipped by the
  // compact 40px field height. Desktop keeps the default resting behaviour.
  public mobileFloatLabel: FloatLabelType = 'auto';

  constructor(
    private formBuilder: FormBuilder,
    private utils: UtilityService,
    private validatorService: WizerValidatorService,
    private currencyFormatter: CurrencyFormatterService,
    private breakpointObserver: BreakpointObserver,
    private authService: AuthService,
    private permissionService: PermissionService,
  ) {
    this.reportsForm = this.formBuilder.group({});
    this.breakpointObserver
      .observe('(max-width: 499.98px)')
      .subscribe(
        (res) => (this.mobileFloatLabel = res.matches ? 'always' : 'auto'),
      );
  }

  private toNumber(value: any): number {
    return typeof value === 'string' ? parseFloat(value) : value;
  }

  /** In the mobile pill, abbreviate stat values from a million (e.g.
   *  ₦173,155,000 -> ₦173.16M). Non-numeric values (e.g. "-") are left as-is. */
  public isSummaryAbbreviated(item: reportSummaryType): boolean {
    if (item.type !== 'number' && item.type !== 'currency') return false;
    const num = this.toNumber(item.value);
    return !isNaN(num) && Math.abs(num) >= 1_000_000;
  }

  public summaryAbbrev(item: reportSummaryType): string {
    const num = this.toNumber(item.value);
    const abs = Math.abs(num);

    let divisor: number;
    let suffix: string;
    if (abs >= 1e12) {
      divisor = 1e12;
      suffix = 'T';
    } else if (abs >= 1e9) {
      divisor = 1e9;
      suffix = 'B';
    } else {
      divisor = 1e6;
      suffix = 'M';
    }

    const short = (num / divisor).toFixed(2).replace(/\.?0+$/, '');
    const symbol =
      item.type === 'currency'
        ? this.currencyFormatter.getCurrencySymbol()
        : '';

    return `${symbol}${short}${suffix}`;
  }

  ngOnInit(): void {
    this.initForm();
    this.loadSavedData();
    this.applyUserScopeDefaults();
    this.dateListner();
  }

  private applyUserScopeDefaults(): void {
    this.canViewOtherAreas = this.permissionService.hasPermissionList([
      Permissions.CAN_VIEW_OTHER_AREAS,
    ]);
    this.canViewOtherBranches = this.permissionService.hasPermissionList([
      Permissions.CAN_VIEW_OTHER_BRANCHES,
    ]);
    this.canViewLoanOfficerFilter = this.permissionService.hasPermissionList([
      Permissions.CAN_VIEW_OTHER_LOANOFFICERS,
      Permissions.CAN_VIEW_OTHER_AREAS,
      Permissions.CAN_VIEW_OTHER_BRANCHES,
    ]);

    const user = this.authService.getuser;
    if (!user) return;

    // Lock area to the logged-in user's assigned area.
    if (!this.canViewOtherAreas && user.area?.id) {
      this.defaultArea = { id: user.area.id, name: user.area.name };
      this.selectedAreaId = user.area.id;
      this.reportsForm.get(ReportInputTypes.AREA)?.setValue(user.area.id, { emitEvent: false });
      this.setSelectedFilter.emit({ type: ReportInputTypes.AREA, value: this.defaultArea });
    }

    // Lock branch to the logged-in user's assigned branch.
    if (!this.canViewOtherAreas && !this.canViewOtherBranches && user.branch?.id) {
      this.defaultBranch = { id: user.branch.id, name: user.branch.name };
      this.selectedBranchId = user.branch.id;
      this.reportsForm.get(ReportInputTypes.BRANCH)?.setValue(user.branch.id, { emitEvent: false });
      this.setSelectedFilter.emit({ type: ReportInputTypes.BRANCH, value: this.defaultBranch });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['summaryData']) {
      const data = this.summaryData || [];
      this.statItems = data.filter(
        (i) => i.type === 'number' || i.type === 'currency',
      );
      this.dateItems = data.filter(
        (i) => i.type !== 'number' && i.type !== 'currency',
      );
    }
  }

  initForm() {
    this.inputsToShow.forEach((input) => {
      this.reportsForm.addControl(input, new FormControl());
      //this.reportsForm.controls[input].addValidators(Validators.required)
    });
  }

  loadSavedData(): void {
    this.reportsForm.patchValue(this.savedData);
    if (this.savedData?.areaId) this.selectedAreaId = this.savedData.areaId;
    if (this.savedData?.branchId) this.selectedBranchId = this.savedData.branchId;
  }

  runReport() {
    if (!this.reportsForm.valid) {
      this.validatorService.CheckMessagesAndNotify(this.reportsForm.controls);
      return;
    }
    // getRawValue() includes disabled controls (e.g. locked area/branch
    // dropdowns). reportsForm.value omits them, which serialises as "undefined"
    // in the query string.
    const rawValues = this.reportsForm.getRawValue();
    const payload = {};
    this.inputsToShow.forEach((input) => {
      if (this.hasDateTypes(input)) {
        payload[input] = this.utils.formatDate_custom(rawValues[input]);
        return;
      }
      payload[input] = rawValues[input];
    });

    this.runReportEmit.emit(payload);
  }

  public emitInputedVal(key: string, value: string) {
    this.setInputedFilter.emit({ key, value });
  }

  public emitSelectedVal(
    key: ReportInputTypes,
    val: { id: any; name: string },
  ) {
    if (!val) val = this.defaultAll;
    switch (key) {
      case ReportInputTypes.AREA:
        this.selectedAreaId = val.id;
        if (val.id === 0) {
          this.setSelectedFilter.emit({
            type: ReportInputTypes.BRANCH,
            value: this.selectedBranchId,
          });
        } else {
          //set form value to All
          this.reportsForm.get(ReportInputTypes.BRANCH)?.setValue(0);
          this.reportsForm.get(ReportInputTypes.LOAN_OFFICER)?.setValue(0);

          //set behaviour subject to hold the current values
          this.setSelectedFilter.emit({
            type: ReportInputTypes.BRANCH,
            value: this.defaultAll,
          });
          this.setSelectedFilter.emit({
            type: ReportInputTypes.LOAN_OFFICER,
            value: this.defaultAll,
          });
          this.setSelectedFilter.emit({
            type: ReportInputTypes.GROUP,
            value: this.defaultAll,
          });
        }
        this.setSelectedFilter.emit({
          type: ReportInputTypes.AREA,
          value: val,
        });
        break;
      case ReportInputTypes.BRANCH:
        this.selectedBranchId = val.id;
        if (val.id !== 0) {
          this.reportsForm.get(ReportInputTypes.LOAN_OFFICER)?.setValue(0);
          this.setSelectedFilter.emit({
            type: ReportInputTypes.LOAN_OFFICER,
            value: this.defaultAll,
          });
        }
        this.setSelectedFilter.emit({
          type: ReportInputTypes.BRANCH,
          value: val,
        });
        break;
      case ReportInputTypes.GROUP:
        this.setSelectedFilter.emit({
          type: ReportInputTypes.GROUP,
          value: val,
        });
        break;
      case ReportInputTypes.LOAN_OFFICER:
        this.setSelectedFilter.emit({
          type: ReportInputTypes.LOAN_OFFICER,
          value: val,
        });
        break;
      case ReportInputTypes.LOAN_PRODUCT:
        this.setSelectedFilter.emit({
          type: ReportInputTypes.LOAN_PRODUCT,
          value: val,
        });
        break;
      case ReportInputTypes.DEPOSIT_PRODUCT:
        this.setSelectedFilter.emit({
          type: ReportInputTypes.DEPOSIT_PRODUCT,
          value: val,
        });
        break;
      case ReportInputTypes.LOAN_STATUS:
        this.setSelectedFilter.emit({
          type: ReportInputTypes.LOAN_STATUS,
          value: val,
        });
        break;
      case ReportInputTypes.LOAN_STATE:
        this.setSelectedFilter.emit({
          type: ReportInputTypes.LOAN_STATE,
          value: val,
        });
        break;
      case ReportInputTypes.LOAN_CLASSIFICATION:
        this.setSelectedFilter.emit({
          type: ReportInputTypes.LOAN_CLASSIFICATION,
          value: val,
        });
        break;
      case ReportInputTypes.LOAN_RISK_CLASSIFICATION:
        this.setSelectedFilter.emit({
          type: ReportInputTypes.LOAN_RISK_CLASSIFICATION,
          value: val,
        });
        break;
      case ReportInputTypes.ACCOUNT_STATE:
        this.setSelectedFilter.emit({
          type: ReportInputTypes.ACCOUNT_STATE,
          value: val,
        });
        break;
      case ReportInputTypes.CUSTOMER_TYPE:
        this.setSelectedFilter.emit({
          type: ReportInputTypes.CUSTOMER_TYPE,
          value: val,
        });
        break;
      case ReportInputTypes.CLIENT:
        this.setSelectedFilter.emit({
          type: ReportInputTypes.CLIENT,
          value: val,
        });
        break;
      case ReportInputTypes.DEPOSIT_CLIENT:
        this.setSelectedFilter.emit({
          type: ReportInputTypes.DEPOSIT_CLIENT,
          value: val,
        });
    }
    this.emitChange.emit({ key, val });
  }

  byInputType(index: number, value) {
    return value;
  }

  get getAreaCtrl() {
    return this.reportsForm.get(ReportInputTypes.AREA) as FormControl;
  }

  get getBranchCtrl() {
    return this.reportsForm.get(ReportInputTypes.BRANCH) as FormControl;
  }

  get getLoanOfficerCtrl() {
    return this.reportsForm.get(ReportInputTypes.LOAN_OFFICER) as FormControl;
  }

  get getGroupCtrl() {
    return this.reportsForm.get(ReportInputTypes.GROUP) as FormControl;
  }

  get getLimitCtrl() {
    return this.reportsForm.get(ReportInputTypes.LIMIT) as FormControl;
  }

  get getProductCtrl() {
    return this.reportsForm.get(ReportInputTypes.LOAN_PRODUCT) as FormControl;
  }

  get getDepositProductCtrl() {
    return this.reportsForm.get(
      ReportInputTypes.DEPOSIT_PRODUCT,
    ) as FormControl;
  }

  get getClientCtrl() {
    return this.reportsForm.get(ReportInputTypes.CLIENT) as FormControl;
  }
  get getDepositClientCtrl() {
    return this.reportsForm.get(ReportInputTypes.DEPOSIT_CLIENT) as FormControl;
  }

  get getStatusCtrl() {
    return this.reportsForm.get(ReportInputTypes.LOAN_STATUS) as FormControl;
  }

  get getLoanStateCtrl() {
    return this.reportsForm.get(ReportInputTypes.LOAN_STATE) as FormControl;
  }

  get getLoanClassificationCtrl() {
    return this.reportsForm.get(
      ReportInputTypes.LOAN_CLASSIFICATION,
    ) as FormControl;
  }

  get getLoanRiskClassificationCtrl() {
    return this.reportsForm.get(
      ReportInputTypes.LOAN_RISK_CLASSIFICATION,
    ) as FormControl;
  }

  get getAccountStateCtrl() {
    return this.reportsForm.get(ReportInputTypes.ACCOUNT_STATE) as FormControl;
  }

  get getCustomerTypeCtrl() {
    return this.reportsForm.get(ReportInputTypes.CUSTOMER_TYPE) as FormControl;
  }

  public dateListner() {
    // Adjust date picker ranges only when both start and end dates are present and have values
    if (this.hasStartAndEndDate(this.inputsToShow)) {
      const startVal = this.reportsForm.get(ReportInputTypes.START_DATE).value;
      const endVal = this.reportsForm.get(ReportInputTypes.END_DATE).value;

      if (endVal) {
        this.startDateRange.max = new Date(endVal);
        this.setSelectedFilter.emit({
          type: ReportInputTypes.END_DATE,
          value: this.startDateRange.max,
        });
      }
      if (startVal) {
        this.endDateRange.min = new Date(startVal);
        this.setSelectedFilter.emit({
          type: ReportInputTypes.START_DATE,
          value: this.endDateRange.min,
        });
      }
    } else {
      // Default unrestricted ranges
      this.startDateRange.max = new Date('2100-12-31');
      this.endDateRange.min = new Date('1900-01-01');
    }
  }

  onRunMenu() {
    this.runMainMenu.emit(this.reportsForm.getRawValue());
  }

  onRunMenuChange(val: string) {
    this.runMenuChange.emit({ val, formValues: this.reportsForm.getRawValue() });
  }

  onSearch(value: string) {
    this.searchEmit.emit(value);
  }

  onStateChange(value: any) {
    this.stateEmit.emit(value);
  }

  onActionTrigger(val: ActionButtonType['value']) {
    this.actionEmit.emit(val);
  }

  hasDateTypes(input: ReportInputTypes): boolean {
    return (
      input === ReportInputTypes.START_DATE ||
      input === ReportInputTypes.END_DATE ||
      input === ReportInputTypes.INACTIVE_SINCE
    );
  }

  hasStartAndEndDate(inputsArray: ReportInputTypes[]): boolean {
    return (
      inputsArray.includes(ReportInputTypes.START_DATE) &&
      inputsArray.includes(ReportInputTypes.END_DATE)
    );
  }

  isReportInputTypePresent(inputType: ReportInputTypes): boolean {
    return this.inputsToShow.includes(inputType);
  }
}
